/* =====================================================================
   Vidalite — Learn "Ask" instant-answer search
   ---------------------------------------------------------------------
   As the visitor types a question we surface, in one panel:
     • GUIDES   — the article that answers the question, with its excerpt
                  shown as the answer snippet.
     • PRODUCTS — matching fixtures (thumbnail, type, price).

   GUIDES are matched against a small in-page index built from the blog's
   real titles, tags and excerpts (emitted by main-blog.liquid), scored
   client-side with a lighting-domain synonym map. This is deliberate:
   Shopify's predictive endpoint only matches titles, so conceptual
   questions ("color temperature", "glare", "warm light") return nothing
   from it. The local index answers those from the guides' own words.

   PRODUCTS use Shopify predictive search, which covers the whole catalog.
   Enter / the Ask button fall back to the full /search results page.

   Security: results are rendered with textContent / safe DOM building.
   Excerpts are decoded with DOMParser (no script exec, no resource loads)
   and never injected as HTML. Race safety: stale predictive requests are
   aborted and a lastQuery guard prevents an old response overwriting a
   newer one.
   ===================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-ask]');
  if (!root) return;

  var input = root.querySelector('[data-ask-input]');
  var panel = root.querySelector('[data-ask-panel]');
  var form = root.querySelector('[data-ask-form]');
  if (!input || !panel || !form) return;

  var searchUrl = root.getAttribute('data-search-url') || '/search';
  var MIN = 2;
  var DEBOUNCE = 150;
  var LIMIT = 4;

  // ---- local guide index ----------------------------------------------
  var GUIDES = [];
  try {
    var idx = root.querySelector('[data-ask-guides]');
    if (idx) GUIDES = JSON.parse(idx.textContent) || [];
  } catch (e) { GUIDES = []; }

  // Stopwords stripped from the question before scoring.
  var STOP = {
    the: 1, a: 1, an: 1, for: 1, to: 1, of: 1, in: 1, on: 1, my: 1, is: 1,
    are: 1, what: 1, which: 1, how: 1, do: 1, does: 1, i: 1, should: 1,
    use: 1, best: 1, and: 1, or: 1, with: 1, me: 1, you: 1, can: 1, need: 1,
    room: 1, light: 1, lighting: 1, lights: 1
  };

  // Lighting-domain synonym expansion: a typed term also contributes the
  // mapped concept words so questions reach the guide that covers them.
  var SYN = {
    warm: 'color temperature', cool: 'color temperature', soft: 'color temperature',
    daylight: 'color temperature', kelvin: 'color temperature', cct: 'color temperature',
    white: 'color temperature', ambiance: 'color temperature', mood: 'color temperature',
    pot: 'recessed downlight', canless: 'recessed downlight', wafer: 'recessed downlight',
    pinhole: 'recessed downlight', ceiling: 'recessed downlight', spacing: 'recessed downlight',
    spotlight: 'spot track', rail: 'track', heads: 'track', adjustable: 'track',
    brass: 'finish', bronze: 'finish', nickel: 'finish', chrome: 'finish',
    copper: 'finish', matte: 'finish', black: 'finish', metal: 'finish', colour: 'finish'
  };

  GUIDES.forEach(function (g) {
    g._title = (g.title || '').toLowerCase();
    g._tags = (g.tags || []).join(' ').toLowerCase();
    g._exc = (g.excerpt || '').toLowerCase();
  });

  function scoreGuides(q) {
    var raw = q.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/);
    var toks = [], seen = {};
    raw.forEach(function (t) {
      if (!t || STOP[t]) return;
      if (!seen[t]) { seen[t] = 1; toks.push(t); }
      if (SYN[t]) SYN[t].split(' ').forEach(function (s) { if (!seen[s]) { seen[s] = 1; toks.push(s); } });
    });
    if (!toks.length) return [];
    return GUIDES.map(function (g) {
      var score = 0;
      toks.forEach(function (t) {
        if (t.length < 3) return;
        if (g._title.indexOf(t) > -1) score += 3;
        else if (g._tags.indexOf(t) > -1) score += 2;
        else if (g._exc.indexOf(t) > -1) score += 1;
      });
      return { g: g, score: score };
    }).filter(function (x) { return x.score > 0; })
      .sort(function (a, b) { return b.score - a.score; })
      .slice(0, LIMIT)
      .map(function (x) {
        return { url: x.g.url, title: x.g.title, image: x.g.image, snippet: tidy(x.g.excerpt) };
      });
  }

  // ---- helpers ---------------------------------------------------------
  function decodeText(html) {
    if (!html) return '';
    try {
      var doc = new DOMParser().parseFromString(String(html), 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (e) {
      return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
  }

  function tidy(text) {
    var s = decodeText(text);
    if (!s) return '';
    s = s.replace(/\s*[—–]\s*/g, ' · ');   // brand: no em/en dashes, use middle dot
    s = s.replace(/[\s,;:·–—-]+$/, '');     // trim trailing separators
    if (s && !/[.!?]$/.test(s)) s += '…';
    return s;
  }

  function safeImg(url, alt) {
    var img = document.createElement('img');
    img.className = 'learn__ask-opt-thumb';
    img.loading = 'eager';
    img.decoding = 'async';
    img.alt = alt || '';
    if (url && /^(https?:)?\/\//.test(url)) {
      // small, retina-crisp thumbnail from the Shopify CDN
      if (!/[?&]width=/.test(url)) url += (url.indexOf('?') === -1 ? '?' : '&') + 'width=132';
      img.src = url;
    }
    return img;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // A labelled group inside the listbox (listbox > group > option is valid
  // ARIA, and keeps the "Guides"/"Products" headings out of the option flow).
  function renderGroup(label, items, build) {
    var group = document.createElement('div');
    group.className = 'learn__ask-group-wrap';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', label);
    group.appendChild(el('p', 'learn__ask-group', label));
    items.forEach(function (it) {
      var opt = build(it);
      group.appendChild(opt);
      options.push(opt);
    });
    panel.appendChild(group);
  }

  function formatPrice(price) {
    var n = Number(price);
    if (!isFinite(n)) return '';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  // ---- panel state -----------------------------------------------------
  var options = [];        // rendered <a role=option> elements, in nav order
  var activeIndex = -1;
  var lastQuery = '';
  var optSeq = 0;
  var timer = null;
  var controller = null;

  // Only allow http(s) or same-origin relative paths to become an href, so a
  // hostile predictive-search payload can't smuggle a javascript:/data: URL
  // into a link the user activates with Enter or a click.
  function safeHref(href) {
    return (href && /^(https?:\/\/|\/)/.test(href)) ? href : '#';
  }

  function makeOption(href) {
    var a = document.createElement('a');
    a.className = 'learn__ask-opt';
    a.href = safeHref(href);
    a.id = 'learn-ask-opt-' + (optSeq++);
    a.setAttribute('role', 'option');
    a.setAttribute('aria-selected', 'false');
    return a;
  }

  function clearPanel() {
    panel.textContent = '';
    panel.setAttribute('aria-label', 'Answers and products');
    options = [];
    activeIndex = -1;
    input.removeAttribute('aria-activedescendant');
  }

  function openPanel() {
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function closePanel() {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    clearPanel();
  }

  function renderState(cls, message) {
    clearPanel();
    panel.appendChild(el('p', 'learn__ask-state ' + cls, message));
    openPanel();
  }

  function appendAllResults(q) {
    var all = makeOption(searchUrl + '?q=' + encodeURIComponent(q) + '&type=article,product,page&options%5Bprefix%5D=last');
    all.className = 'learn__ask-opt learn__ask-all';
    all.appendChild(el('span', null, 'See all results for "' + q + '"'));
    all.appendChild(el('span', 'learn__ask-all-arrow', '→'));
    panel.appendChild(all);
    options.push(all);
  }

  // ---- rendering -------------------------------------------------------
  function renderResults(q, guides, products) {
    clearPanel();

    if (!guides.length && !products.length) {
      var empty = el('div', 'learn__ask-empty');
      empty.appendChild(el('p', 'learn__ask-state is-empty', 'No instant match for "' + q + '".'));
      panel.appendChild(empty);
      appendAllResults(q);
      openPanel();
      return;
    }

    if (guides.length) {
      renderGroup('Guides', guides, function (g) {
        var opt = makeOption(g.url);
        opt.classList.add('learn__ask-opt--guide');
        opt.appendChild(safeImg(g.image, g.title));
        var body = el('span', 'learn__ask-opt-body');
        body.appendChild(el('span', 'learn__ask-opt-title', g.title));
        if (g.snippet) body.appendChild(el('span', 'learn__ask-opt-snip', g.snippet));
        opt.appendChild(body);
        opt.appendChild(el('span', 'learn__ask-opt-kind', 'Guide'));
        return opt;
      });
    }

    var prods = products.slice(0, LIMIT);
    if (prods.length) {
      renderGroup('Products', prods, function (p) {
        var opt = makeOption(p.url);
        opt.classList.add('learn__ask-opt--product');
        opt.appendChild(safeImg(p.featured_image && p.featured_image.url ? p.featured_image.url : p.image, p.title));
        var body = el('span', 'learn__ask-opt-body');
        body.appendChild(el('span', 'learn__ask-opt-title', p.title));
        if (p.type) body.appendChild(el('span', 'learn__ask-opt-snip', p.type));
        opt.appendChild(body);
        if (p.price != null && p.price !== '') {
          opt.appendChild(el('span', 'learn__ask-opt-price', formatPrice(p.price)));
        }
        return opt;
      });
    }

    // Announce result counts to assistive tech (the visual group headings are
    // not exposed as options).
    panel.setAttribute('aria-label',
      guides.length + (guides.length === 1 ? ' guide, ' : ' guides, ') +
      prods.length + (prods.length === 1 ? ' product' : ' products'));

    appendAllResults(q);
    openPanel();
  }

  // ---- predictive fetch (products + supplemental articles) ------------
  function fetchPredictive(q, signal) {
    var url = searchUrl + '/suggest.json'
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=product,article'
      + '&resources[limit]=' + LIMIT
      + '&resources[options][unavailable_products]=last';
    return fetch(url, { headers: { 'Accept': 'application/json' }, signal: signal })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var rs = (d && d.resources && d.resources.results) || {};
        return { products: rs.products || [], articles: rs.articles || [] };
      });
  }

  function runQuery(q) {
    var localGuides = scoreGuides(q);

    // Phase 1 — answer instantly from the local guide index (synchronous).
    // Products fill in when predictive resolves. If no guide matches yet,
    // keep the "Searching…" state until the network comes back.
    if (localGuides.length) renderResults(q, localGuides, []);

    if (controller) controller.abort();
    controller = ('AbortController' in window) ? new AbortController() : null;

    fetchPredictive(q, controller ? controller.signal : undefined)
      .then(function (res) {
        if (q !== lastQuery) return;
        var guides = localGuides.length ? localGuides : res.articles.map(function (a) {
          return { url: a.url, title: a.title, image: a.image, snippet: tidy(a.summary_html) };
        });
        renderResults(q, guides, res.products);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (q !== lastQuery) return;
        // Predictive failed. Local guides (if any) already rendered in phase 1.
        // With none, show the full-search escape hatch rather than vanishing.
        if (!localGuides.length) renderResults(q, [], []);
      });
  }

  // ---- active-option navigation ---------------------------------------
  function setActive(i) {
    if (!options.length) return;
    if (activeIndex > -1 && options[activeIndex]) {
      options[activeIndex].classList.remove('is-active');
      options[activeIndex].setAttribute('aria-selected', 'false');
    }
    activeIndex = (i + options.length) % options.length;
    var opt = options[activeIndex];
    opt.classList.add('is-active');
    opt.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', opt.id);
    opt.scrollIntoView({ block: 'nearest' });
  }

  // ---- events ----------------------------------------------------------
  input.addEventListener('input', function () {
    var q = input.value.trim();
    lastQuery = q;
    if (timer) clearTimeout(timer);
    if (q.length < MIN) {
      if (controller) controller.abort();
      closePanel();
      return;
    }
    renderState('is-loading', 'Searching…');
    timer = setTimeout(function () { runQuery(q); }, DEBOUNCE);
  });

  input.addEventListener('keydown', function (e) {
    var open = !panel.hidden;
    if (e.key === 'ArrowDown') {
      if (!open) return;
      e.preventDefault();
      setActive(activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      if (!open) return;
      e.preventDefault();
      setActive(activeIndex - 1);
    } else if (e.key === 'Home') {
      if (open && options.length) { e.preventDefault(); setActive(0); }
    } else if (e.key === 'End') {
      if (open && options.length) { e.preventDefault(); setActive(options.length - 1); }
    } else if (e.key === 'Enter') {
      if (open && activeIndex > -1 && options[activeIndex]) {
        e.preventDefault();
        window.location.href = options[activeIndex].href;
      }
      // otherwise let the form submit handler send to full search
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); closePanel(); }
    } else if (e.key === 'Tab') {
      // close the listbox as focus leaves the input (combobox pattern);
      // do NOT preventDefault so focus moves to the next control naturally.
      if (open) closePanel();
    }
  });

  panel.addEventListener('mousemove', function (e) {
    var opt = e.target.closest('.learn__ask-opt');
    if (!opt) return;
    var i = options.indexOf(opt);
    if (i > -1 && i !== activeIndex) setActive(i);
  });

  input.addEventListener('focus', function () {
    if (input.value.trim().length >= MIN && options.length) openPanel();
  });

  document.addEventListener('click', function (e) {
    if (!root.contains(e.target)) closePanel();
  });

  // Ask button / Enter without a highlighted row -> full search results.
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var q = input.value.trim();
    if (!q) return;
    window.location.href = searchUrl + '?q=' + encodeURIComponent(q)
      + '&type=article,product,page&options%5Bprefix%5D=last';
  });
})();
